import { Task } from "@emberly/zenith-client";
import * as turf from "@turf/turf";
import EventEmitter from "events";

const TEST_HARNESS = false;

export default class GeoLocationProvider extends EventEmitter {

  constructor(core) {
    super();
    this._core = core;
    this._api = TEST_HARNESS ? new GeoLocationTestHarness(core) : navigator.geolocation;

    this._route = null;
    this.ready = false;
    this._destroyed = false;

    this._lastLocationReceived = Date.now();
    this._gpsFixLost = false;
    this._apiHandle = null;

    this._prevLocation = null;
    this._lastLocation = null;
    this._initLocationTask = new Task();
    this._lastTick = 0;

    this._lastPointOnLine = null;
    this._prevPointOnLine = null;
    this._geolocDeltaTime = 0;
    this._routeLength = 0;
    this._lastKm = 0;

    this._bearing = 0;

    this._geolocVelocity = 0;

    this._skipFrame = false;

    this._lastRouteTime = 0;

    this._geolocationError = false;

    this._onTick = (dt) => this.tick(dt);

    this._gpsFixTimeout = 15;
    this._minAccuracyM = 250;
    this._gpsFixCount = 0;

    this.setup();
  }

  get bearing() {
    return this._bearing;
  }

  gpsReady() {
    return this._gpsFixCount >= 2;
  }

  clear() {
    this._route = null;
  }

  async setup() {
    this.ready = true;

    this._apiHandle = this._api.watchPosition(
      (pos) => {
        if (pos !== null && pos.coords.accuracy < this._minAccuracyM) {
          this.onLocation(pos);
        }
      },
      (err) => this.onError(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 250 }
    )

    this.watchPosition();

    requestAnimationFrame(this._onTick);
  }

  async watchPosition() {

    this._lastLocationReceived = Date.now();
    this._gpsFixLost = false;

    while (true) {

      //console.time("gloc");
      const pos = await this.getCurrentPosition();
      //console.timeEnd("gloc");

      //console.log(pos);

      await Task.Delay(500);
      if (!this._destroyed && this.ready) {

        if (pos !== null && pos.coords.accuracy < this._minAccuracyM) {
          this.onLocation(pos);
        }

      } else {
        break;
      }
    }
  }

  getCurrentPosition() {
    let task = new Task();

    this._api.getCurrentPosition(
      gloc => task.complete(gloc),
      err => task.complete(null),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 250 });

    return task.wait();
  }

  hasGPSFix() {
    return !this._gpsFixLost;
  }

  hasRoute() {
    return !!this._route;
  }

  onLocation(pos) {
    //console.log(`${Date.now() - this._lastLocationReceived} ms`);
    this._lastLocationReceived = Date.now();

    if (!this._destroyed) {
      this.ready = true;

      if (!!pos && !!this._geolocationError) {
        this._geolocationError = false;
        this.emit("geolocationerror", false);
      }

      if (this._lastLocation === null) {
        this._lastLocation = pos;
        this._initLocationTask.complete(pos);
      } else {

        let ts = pos.timestamp;

        // Dont accept older coords
        if (ts <= this._lastLocation.timestamp) {
          return;
        }

        this._geolocDeltaTime = (ts - this._lastLocation.timestamp) / 1000.0; // seconds
      }


      if (this.hasRoute()) {

        var pt = this.toPoint(pos);

        this._prevPointOnLine = this._lastPointOnLine;
        this._lastPointOnLine = this._route.nearestPointOnLine(pt);

        const acc = Math.min(250, pos.coords.accuracy);
        const distFromLineMeters = Math.max(0, this._lastPointOnLine.properties.dist * 1000.0 - acc);
        const timeSinceLastRoute = (Date.now() - this._lastRouteTime) / 1000.0;

        this._prevLocation = this._lastLocation;
        this._lastLocation = pos;

        if (distFromLineMeters < 45 || timeSinceLastRoute < 7) {
          // Continue along route

          this._lastKm = this._lastPointOnLine.properties.location;

          if (this._geolocDeltaTime !== 0 && !!this._prevPointOnLine) {

            let a = turf.point(this._prevPointOnLine.geometry.coordinates);
            let b = turf.point(this._lastPointOnLine.geometry.coordinates);

            let distance = turf.distance(a, b) * 1000.0; // meters
            let velocity = distance / this._geolocDeltaTime; // m/s

            this._geolocVelocity = velocity; // TODO here clamp by route.getDistance() * 1.25
          }

        } else {
          // Reroute
          this.reroute();
        }


      } else {
        this._prevLocation = this._lastLocation;
        this._lastLocation = pos;
        this.emit("location", pos, 0, pos?.coords?.heading || this._bearing);
      }

      this._gpsFixCount++;

      if (this._gpsFixCount === 2) {
        this.emit("gpsready", true);
      }
    }

  }


  toPoint(pos) {
    return turf.point([pos.coords.longitude, pos.coords.latitude]);
  }

  tick(dt) {
    if (this._destroyed) return;

    // Limit fps
    if (this._skipFrame) {
      this._skipFrame = false;
      requestAnimationFrame(this._onTick);
      return;
    }

    this._skipFrame = true;

    // handle lost gps fix
    const gpsDeltaTime = (Date.now() - this._lastLocationReceived) / 1000.0;

    if (this._gpsFixLost && gpsDeltaTime < this._gpsFixTimeout) {
      this._gpsFixLost = false;
      this.emit("gpsfix", true);
    } else if (!this._gpsFixLost && gpsDeltaTime >= this._gpsFixTimeout) {
      this._gpsFixLost = true;
      this.emit("gpsfix", false);
    }


    // Interplate positions
    if (this._lastTick !== 0 && !!this._lastLocation && !!this._prevLocation && !!this._lastPointOnLine) {
      const deltaTime = (dt - this._lastTick) / 1000.0; // seconds

      if (this.hasRoute()) {

        // Continue here, interpolate ahead along the route based on velocity

        const routeSpeed = this._route.speed(this._lastKm);

        // if more than 5 seconds and measured velocity is larger than half of routespeed since last geoloc, use routespeed, if not clamp between 1.35x routespeed and measured velocity
        const metersToTravel = (
          this._geolocVelocity > routeSpeed * 0.5 && gpsDeltaTime > 5 ?
            routeSpeed :
            Math.min(routeSpeed * 1.5, this._geolocVelocity)
        ) * deltaTime;

        // convert to kilometres
        const kmToTravel = metersToTravel / 1000.0;

        // where along the route we are right now (in km)
        //const lastKm = this._lastPointOnLine.properties.location;

        // clamp to the end of the route
        const nextKm = Math.min(this._lastKm + kmToTravel, this._route.lengthKm);

        // get a Turf point at that distance
        const nextPointOnLine = this._route.along(nextKm);

        // build a Position‐like object
        const predictedPos = {
          coords: {
            latitude: nextPointOnLine.geometry.coordinates[1],
            longitude: nextPointOnLine.geometry.coordinates[0]
          }
        };

        if (nextKm > this._lastKm + 0.0001) {
          this._bearing = turf.bearing(this._lastPointOnLine, nextPointOnLine);
        }


        this._lastKm = nextKm;
        //console.log(predictedPos)
        // emit it (or do whatever you need with it)
        this.emit("location", predictedPos, nextKm, this._bearing);
      }
    }

    this._lastTick = dt;
    requestAnimationFrame(this._onTick);
  }

  onError(err) {
    if (!this._geolocationError && err.code === 1) {
      console.log(err);
      this._geolocationError = true;
      this.emit("geolocationerror", true);
    }
  }

  hasError() {
    return this._geolocationError;
  }

  destroy() {
    this.removeAllListeners();
    this._destroyed = true;

    if (this._apiHandle !== null) {
      this._api.clearWatch(this._apiHandle);
      this._apiHandle = null;
    }
  }

  async getLocation() {

    if (this._lastLocation === null) {
      return this._initLocationTask.wait();
    }

    return this._lastLocation;
  }

  setRoute(route) {
    this._lastRouteTime = Date.now();
    this._route = route;
    this._lastPointOnLine = null;
    this._prevPointOnLine = null;
    this._geolocVelocity = 0;
    this._bearing = route.getBearing(0);

    if (TEST_HARNESS) {
      this._api.setRoute(route);
    }
  }

  reroute() {
    this._route = null;
    this._core.reroute();
    console.log("re-route");
  }


  cancel() {
    this.clear();

    if (TEST_HARNESS) {
      this._api.cancel();
    }
  }

}


export class GeoLocationTestHarness {

  constructor(core) {
    this._core = core;

    this._origin = {
      coords: {
        longitude: 5.7769129,
        latitude: 58.8529076,
        heading: 0,
        speed: null,
        accuracy: 10,
      }
    };


    this._timer = null;
    this._success = null;
    this._error = null;
    this._options = null;
    this._route = null;
    this._latitude = this._origin.coords.latitude;
    this._longitude = this._origin.coords.longitude;
    this._progress = 0;
    this._heading = 0;

    this._skipNext = false;

    this._lastTick = Date.now();


    /*
    setTimeout(() => {
      core.renderer.mapbox.on("click", ev => {
        this._skipNext = true;
        this._longitude = ev.lngLat.lng;
        this._latitude = ev.lngLat.lat;
      });
    }, 500);
*/

    this._timer = setInterval(() => this.tick(), 100);
  }

  setRoute(route) {
    this._progress = 0;
    this._route = route;
  }
  

  cancel() {
    this._success = null;
    this._route = null;
    this._progress = 0;
    clearInterval(this._timer);
  }

  getCurrentPosition(callback, error, options) {
    setTimeout(() => {
      callback({
        timestamp: Date.now(),
        coords: {
          ...this._origin.coords,
          longitude: this._longitude,
          latitude: this._latitude,
          heading: this._heading
        }
      });
    }, 100);
  }

  watchPosition(success, error, options) {
    this._success = success;
    this._error = error;
    this._options = options;
    return 1;
  }

  tick() {
    const dt = (Date.now() - this._lastTick) / 1000.0;

    if (!!this._route && !this._skipNext && this._progress < this._route.lengthKm) {
      const speedMul = 5.0;
      const speed = this._route.speed(this._progress) * speedMul; // meters per second

      this._progress += (speed / 1000.0) * dt; // kilomters per second * seconds since last tick

      const point = this._route.along(
        this._progress
      ).geometry.coordinates;

      this._longitude = point[0];
      this._latitude = point[1];

      // Snap to end
      if (this._progress > this._route.lengthKm) {
        this._progress = this._route.lengthKm;
      }
    }

    const bearing = this._core.geolocation.bearing;

    this._heading = (bearing * 180 / Math.PI + 360) % 360;
    if (this._heading < 0) this._heading += 360;

    if (!!this._success) {

      let coords = {
        ...this._origin.coords,
        longitude: this._longitude,
        latitude: this._latitude,
        heading: this._heading,
      };

      this._success({ coords, timestamp: Date.now() });
    }

    this._skipNext = false;
    this._lastTick = Date.now();
  }

  clearWatch() {
    this._success = null;
  }

}